import { useMemo, useState } from 'react'
import { useDevices } from './hooks/useDevices'
import { useSensors } from './hooks/useSensors'
import { useDeviceControl } from './hooks/useDeviceControl'
import { useViewport } from './hooks/useViewport'
import { useLiveClock } from './hooks/useLiveClock'
import { useRefresh } from './hooks/useRefresh'
import { useAuth } from './contexts/AuthContext'
import { LoginPage } from './components/LoginPage'
import { AppHeader } from './components/AppHeader'
import { PageTitle } from './components/PageTitle'
import { WeatherCard } from './components/WeatherCard'
import { SectionHead } from './components/SectionHead'
import { CompactTile } from './components/CompactTile'
import { SensorTile } from './components/SensorTile'
import { EmptyState, ErrorState, SkeletonGrid } from './components/Placeholders'
import { DetailsPanel } from './components/details/DetailsPanel'
import { ProgrammazioneModal } from './components/ProgrammazioneModal'
import { PinPromptModal } from './components/PinPromptModal'
import { Footer } from './components/Footer'
import { ToastStack } from './components/ToastStack'
import { UpdateAvailable } from './components/UpdateAvailable'
import { useToasts } from './hooks/useToasts'
import type { Device, Sensor } from './types/device'

const SITE_NAME = 'OWW Tradate'

type GridItem =
  | { kind: 'device'; data: Device }
  | { kind: 'sensor'; data: Sensor }

function isController(d: Device): boolean {
  // Anything with a relay the operator can toggle (plain switches and 1PM-style
  // metered switches). The dedicated energy meters (3EM, EM1) come after.
  return d.kind === 'switch' || d.kind === 'switch-metered'
}

function combineForGrid(devices: Device[], sensors: Sensor[]): GridItem[] {
  // 1) ON/OFF actuators (switches), alpha
  // 2) Energy meters (read-only metered devices), alpha
  // 3) Environmental sensors (Sintropy), alpha
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'it')
  const switches = devices.filter(isController).sort(byName)
  const meters = devices.filter((d) => !isController(d)).sort(byName)
  const sortedSensors = [...sensors].sort(byName)
  return [
    ...switches.map((d): GridItem => ({ kind: 'device', data: d })),
    ...meters.map((d): GridItem => ({ kind: 'device', data: d })),
    ...sortedSensors.map((s): GridItem => ({ kind: 'sensor', data: s })),
  ]
}

export default function App() {
  const viewport = useViewport()
  const isMobile = viewport === 'mobile'
  const isNarrow = viewport !== 'desktop'

  const { user, isAdmin, loading: authLoading, authDisabled, logout } = useAuth()

  // While we don't yet know auth state, render the cream background only —
  // avoids flashing the dashboard before the redirect to login.
  if (authLoading) {
    return <div className="app-shell" data-viewport={viewport} aria-busy="true" />
  }

  // Auth is configured server-side AND we don't have a session → login screen.
  if (!user && !authDisabled) {
    return <LoginPage />
  }

  return <Dashboard
    viewport={viewport}
    isMobile={isMobile}
    isNarrow={isNarrow}
    user={user}
    isAdmin={isAdmin}
    onLogout={logout}
  />
}

interface DashboardProps {
  viewport: ReturnType<typeof useViewport>
  isMobile: boolean
  isNarrow: boolean
  user: string | null
  isAdmin: boolean
  onLogout: () => void
}

function Dashboard({ viewport, isMobile, isNarrow, user, isAdmin, onLogout }: DashboardProps) {
  const { data: devices, isLoading: devicesLoading, isError: devicesError, refetch } = useDevices()
  const { data: sensors } = useSensors()
  const control = useDeviceControl()
  const liveTime = useLiveClock()
  const { refresh, isFetching } = useRefresh()
  const { push } = useToasts()

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [panelOpen, setPanelOpen] = useState(false)
  const [modalFor, setModalFor] = useState<Device | null>(null)
  // When the user triggers a protected-device action, we stash what they
  // wanted to do (toggle or open schedule) and pop the PIN modal. On confirm
  // we run the action. On cancel we drop the pending intent.
  type PendingAction =
    | { kind: 'toggle'; device: Device; nextOn: boolean }
    | { kind: 'schedule'; device: Device }
  const [pendingProtected, setPendingProtected] = useState<PendingAction | null>(null)
  // Filter — exclusive (tab-like). 'all' = no filter, otherwise restrict to one kind.
  // Clicking the active tab again deselects → back to 'all'.
  const [filter, setFilter] = useState<'all' | 'devices' | 'sensors'>('all')
  const toggleFilter = (kind: 'devices' | 'sensors') => {
    setFilter((prev) => (prev === kind ? 'all' : kind))
  }

  const safeDevices = devices ?? []
  const safeSensors = sensors ?? []

  const items = useMemo(() => combineForGrid(safeDevices, safeSensors), [safeDevices, safeSensors])

  const filteredItems = useMemo(() => {
    if (filter === 'all') return items
    return items.filter((it) => (filter === 'devices' ? it.kind === 'device' : it.kind === 'sensor'))
  }, [items, filter])

  const selection = useMemo<{ kind: 'device'; device: Device } | { kind: 'sensor'; sensor: Sensor } | null>(() => {
    if (!selectedId) return null
    const d = safeDevices.find((x) => x.id === selectedId)
    if (d) return { kind: 'device', device: d }
    const s = safeSensors.find((x) => x.id === selectedId)
    if (s) return { kind: 'sensor', sensor: s }
    return null
  }, [selectedId, safeDevices, safeSensors])

  const toggle = (id: string) => {
    const d = safeDevices.find((x) => x.id === id)
    if (!d) return
    const nextOn = !d.relay?.on
    if (d.isProtected) {
      if (!isAdmin) {
        push('error', 'Operazione riservata agli amministratori')
        return
      }
      setPendingProtected({ kind: 'toggle', device: d, nextOn })
      return
    }
    control.mutate({ id, on: nextOn, channel: d.relay?.channel ?? 0 })
  }

  const selectId = (id: string) => {
    setSelectedId((prev) => (prev === id ? null : id))
    if (isNarrow) setPanelOpen(true)
  }

  const openOverview = () => {
    setSelectedId(null)
    if (isNarrow) setPanelOpen(true)
  }

  const backToOverview = () => setSelectedId(null)
  const closePanel = () => setPanelOpen(false)
  const openSchedule = (d: Device) => {
    if (d.isProtected) {
      if (!isAdmin) {
        push('error', 'Operazione riservata agli amministratori')
        return
      }
      setPendingProtected({ kind: 'schedule', device: d })
      return
    }
    setModalFor(d)
  }

  const totalCount = items.length

  return (
    <div className="app-shell" data-viewport={viewport}>
      <AppHeader
        siteName={SITE_NAME}
        siteKind="Ristorante"
        liveTime={liveTime}
        onSiteClick={openOverview}
        onRefresh={refresh}
        refreshing={isFetching}
        user={user}
        onLogout={onLogout}
      />

      <main className="main main--split" data-viewport={viewport}>
        <PageTitle lastUpdated={liveTime} />
        <WeatherCard />

        <div className="main__split">
          <div className="main__content">
            {devicesLoading && (
              <>
                <SectionHead label="Dispositivi" />
                <SkeletonGrid count={9} isNarrow={isNarrow} />
              </>
            )}

            {devicesError && <ErrorState onRetry={() => refetch()} />}

            {!devicesLoading && !devicesError && totalCount === 0 && <EmptyState />}

            {!devicesLoading && !devicesError && totalCount > 0 && (
              <>
                <SectionHead
                  showPanelButton={isNarrow}
                  onPanelClick={openOverview}
                >
                  <div className="filter-pills" role="tablist" aria-label="Filtra per tipo">
                    <button
                      type="button"
                      role="tab"
                      className="filter-pill"
                      data-active={filter === 'devices'}
                      aria-selected={filter === 'devices'}
                      onClick={() => toggleFilter('devices')}
                    >
                      <span className="filter-pill__dot" />
                      Dispositivi
                    </button>
                    <button
                      type="button"
                      role="tab"
                      className="filter-pill"
                      data-active={filter === 'sensors'}
                      aria-selected={filter === 'sensors'}
                      onClick={() => toggleFilter('sensors')}
                    >
                      <span className="filter-pill__dot" />
                      Sensori
                    </button>
                  </div>
                </SectionHead>
                {filteredItems.length === 0 ? (
                  <div className="ov-empty">Nessun risultato per il filtro selezionato.</div>
                ) : (
                  <section
                    className={`device-grid compact-layout stacked${isNarrow ? ' single-col' : ''}`}
                  >
                    {filteredItems.map((item) =>
                      item.kind === 'device' ? (
                        <CompactTile
                          key={item.data.id}
                          device={item.data}
                          selected={selectedId === item.data.id}
                          isAdmin={isAdmin}
                          onSelect={selectId}
                          onToggle={toggle}
                        />
                      ) : (
                        <SensorTile
                          key={item.data.id}
                          sensor={item.data}
                          selected={selectedId === item.data.id}
                          onSelect={selectId}
                        />
                      ),
                    )}
                  </section>
                )}
              </>
            )}
          </div>

          {!isNarrow && totalCount > 0 && (
            <aside className="main__sidebar">
              <SectionHead
                variant="panel"
                label={selection ? 'Dettaglio' : 'Panoramica'}
                count={
                  selection
                    ? selection.kind === 'device' ? selection.device.name : selection.sensor.name
                    : `sito · ${SITE_NAME}`
                }
              />
              <div className="main__sidebar-inner">
                <DetailsPanel
                  selection={selection}
                  devices={safeDevices}
                  sensors={safeSensors}
                  siteName={SITE_NAME}
                  isAdmin={isAdmin}
                  onClose={() => setSelectedId(null)}
                  onBack={selection ? backToOverview : null}
                  onSelect={selectId}
                  onToggle={toggle}
                  onOpenSchedule={openSchedule}
                  showClose={false}
                  asSheet={false}
                />
              </div>
            </aside>
          )}
        </div>

        {isNarrow && totalCount > 0 && panelOpen && (
          <DetailsPanel
            selection={selection}
            devices={safeDevices}
            sensors={safeSensors}
            siteName={SITE_NAME}
            isAdmin={isAdmin}
            onClose={closePanel}
            onBack={selection ? backToOverview : null}
            onSelect={selectId}
            onToggle={toggle}
            onOpenSchedule={openSchedule}
            showClose={true}
            asSheet={true}
          />
        )}
      </main>

      <Footer />

      {modalFor && (
        <ProgrammazioneModal
          device={modalFor}
          allDevices={safeDevices}
          asSheet={isMobile}
          onClose={() => setModalFor(null)}
        />
      )}

      {pendingProtected && (
        <PinPromptModal
          deviceName={pendingProtected.device.name}
          intent={pendingProtected.kind === 'toggle' ? 'toggle' : 'schedule'}
          toggleSummary={
            pendingProtected.kind === 'toggle'
              ? {
                  fromLabel: pendingProtected.device.relay?.on ? 'ACCESO' : 'SPENTO',
                  toLabel: pendingProtected.nextOn ? 'ACCESO' : 'SPENTO',
                }
              : undefined
          }
          asSheet={isMobile}
          onClose={() => setPendingProtected(null)}
          onConfirmed={() => {
            const action = pendingProtected
            setPendingProtected(null)
            if (action.kind === 'toggle') {
              control.mutate({ id: action.device.id, on: action.nextOn, channel: action.device.relay?.channel ?? 0 })
            } else {
              setModalFor(action.device)
            }
          }}
        />
      )}

      <ToastStack />
      <UpdateAvailable />
    </div>
  )
}
